import { DialogCloseEvent } from './dialog.js';
import { clamp, preventDefault, isMobileBreakpoint } from './utilities.js';
import { Component } from '@theme/component';

const MIN_ZOOM = 1;
const MAX_ZOOM = 5;
const DEFAULT_ZOOM = 1.5;
const DOUBLE_TAP_DELAY = 300;
const DOUBLE_TAP_DISTANCE = 50;
const DRAG_THRESHOLD = 10;
const SWIPE_THRESHOLD = 45;

export class DragZoomWrapper extends Component {
  #controller = new AbortController();

  /** @type {number} */
  #scale = MIN_ZOOM;

  /** @type {number} */
  #initialDistance = 0;

  /** @type {number} */
  #startScale = MIN_ZOOM;

  /** @type {Point} */
  #translate = { x: 0, y: 0 };

  /** @type {Point} */
  #startPosition = { x: 0, y: 0 };

  /** @type {Point} */
  #startTranslate = { x: 0, y: 0 };

  /** @type {boolean} */
  #isDragging = false;

  /** @type {boolean} */
  #initialized = false;

  /** @type {number | null} */
  #animationFrame = null;

  /** @type {number} */
  #lastTapTime = 0;

  /** @type {Point | null} */
  #lastTapPosition = null;

  /** @type {boolean} */
  #hasDraggedBeyondThreshold = false;

  /** @type {boolean} */
  #hasManualZoom = false;

  /** @type {Point} */
  #swipeStart = { x: 0, y: 0 };

  /** @type {Point} */
  #swipeDelta = { x: 0, y: 0 };

  /** @type {boolean} */
  #isSwipeCandidate = false;

  get #image() {
    return this.querySelector('img');
  }

  connectedCallback() {
    super.connectedCallback();

    if (!this.#image) return;

    this.#initResizeListener();

    window.addEventListener(
      DialogCloseEvent.eventName,
      this.#resetZoom
    );

    if (!isMobileBreakpoint()) return;

    this.#initEventListeners();
    this.#updateTransform();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    window.removeEventListener(
      DialogCloseEvent.eventName,
      this.#resetZoom
    );

    this.#controller.abort();
    this.#resizeObserver.disconnect();
    this.#cancelAnimationFrame();
  }

  #initResizeListener() {
    this.#resizeObserver.observe(this);
  }

  #initEventListeners() {
    if (this.#initialized) return;

    this.#initialized = true;

    const { signal } = this.#controller;
    const options = { passive: false, signal };

    this.addEventListener(
      'touchstart',
      this.#handleTouchStart,
      options
    );

    this.addEventListener(
      'touchmove',
      this.#handleTouchMove,
      options
    );

    this.addEventListener(
      'touchend',
      this.#handleTouchEnd,
      options
    );

    this.#updateTransform();
  }

  #handleResize = () => {
    if (!this.#initialized && isMobileBreakpoint()) {
      this.#initEventListeners();
    }

    if (this.#initialized) {
      this.#requestUpdateTransform();
    }
  };

  #resizeObserver = new ResizeObserver(this.#handleResize);

  /**
   * @param {TouchEvent} event
   */
  #handleTouchStart = (event) => {
    const touchCount = event.touches.length;

    // Zwei Finger = Pinch-Zoom
    if (touchCount === 2) {
      preventDefault(event);

      const touch1 = event.touches[0];
      const touch2 = event.touches[1];

      if (!touch1 || !touch2) return;

      this.#isSwipeCandidate = false;
      this.#startZoomGestureFromTouches(touch1, touch2);

      return;
    }

    if (touchCount !== 1) return;

    const touch = event.touches[0];
    if (!touch) return;

    const currentTime = performance.now();
    const timeSinceLastTap =
      currentTime - this.#lastTapTime;

    // Double Tap
    if (
      timeSinceLastTap < DOUBLE_TAP_DELAY &&
      this.#lastTapPosition
    ) {
      const distance = getDistance(
        touch,
        this.#lastTapPosition
      );

      if (distance < DOUBLE_TAP_DISTANCE) {
        preventDefault(event);

        this.#handleDoubleTapFromTouch(touch);

        this.#lastTapTime = 0;
        this.#lastTapPosition = null;
        this.#isSwipeCandidate = false;

        return;
      }
    }

    this.#storeTapInfo(currentTime, touch);

    // Bei 1x wird ein Finger als Swipe interpretiert
    if (this.#scale <= MIN_ZOOM) {
      preventDefault(event);

      this.#swipeStart = {
        x: touch.clientX,
        y: touch.clientY,
      };

      this.#swipeDelta = {
        x: 0,
        y: 0,
      };

      this.#isSwipeCandidate = true;
      this.#isDragging = false;
      this.#hasDraggedBeyondThreshold = false;

      return;
    }

    // Bei Zoom >1x wird ein Finger zum Verschieben genutzt
    preventDefault(event);

    this.#isSwipeCandidate = false;
    this.#startDragGestureFromTouch(touch);
  };

  /**
   * @param {Touch} touch1
   * @param {Touch} touch2
   */
  #startZoomGestureFromTouches(touch1, touch2) {
    this.#initialDistance = getDistance(
      touch1,
      touch2
    );

    this.#startScale = this.#scale;
    this.#isDragging = false;
  }

  /**
   * @param {Touch} touch
   */
  #startDragGestureFromTouch(touch) {
    this.#startPosition = {
      x: touch.clientX,
      y: touch.clientY,
    };

    this.#startTranslate = {
      x: this.#translate.x,
      y: this.#translate.y,
    };

    this.#isDragging = true;
    this.#hasDraggedBeyondThreshold = false;
  }

  /**
   * @param {number} currentTime
   * @param {Touch} touch
   */
  #storeTapInfo(currentTime, touch) {
    this.#lastTapTime = currentTime;

    this.#lastTapPosition = {
      x: touch.clientX,
      y: touch.clientY,
    };
  }

  /**
   * @param {Touch} touch
   */
  #handleDoubleTapFromTouch(touch) {
    const containerCenter = {
      x: this.clientWidth / 2,
      y: this.clientHeight / 2,
    };

    let targetZoom;

    if (this.#hasManualZoom) {
      targetZoom = MIN_ZOOM;
      this.#hasManualZoom = false;

      this.#translate = {
        x: 0,
        y: 0,
      };
    } else {
      const tolerance = 0.05;

      if (
        Math.abs(
          this.#scale - MIN_ZOOM
        ) < tolerance
      ) {
        targetZoom = DEFAULT_ZOOM;
      } else {
        targetZoom = MIN_ZOOM;
      }
    }

    if (targetZoom !== MIN_ZOOM) {
      const oldScale = this.#scale;

      this.#scale = Math.min(
        MAX_ZOOM,
        targetZoom
      );

      const distanceFromCenter = {
        x:
          touch.clientX -
          containerCenter.x,
        y:
          touch.clientY -
          containerCenter.y,
      };

      const scaleDelta =
        this.#scale / oldScale - 1;

      this.#translate.x -=
        (distanceFromCenter.x *
          scaleDelta) /
        this.#scale;

      this.#translate.y -=
        (distanceFromCenter.y *
          scaleDelta) /
        this.#scale;
    } else {
      this.#scale = MIN_ZOOM;

      this.#translate = {
        x: 0,
        y: 0,
      };
    }

    this.#requestUpdateTransform();
  }

  /**
   * @param {TouchEvent} event
   */
  #handleTouchMove = (event) => {
    const touchCount = event.touches.length;

    // Zwei Finger = Pinch-Zoom
    if (touchCount === 2) {
      preventDefault(event);

      const touch1 = event.touches[0];
      const touch2 = event.touches[1];

      if (touch1 && touch2) {
        this.#isSwipeCandidate = false;

        this.#processZoomGesture(
          touch1,
          touch2
        );
      }

      return;
    }

    if (touchCount !== 1) return;

    const touch = event.touches[0];
    if (!touch) return;

    // Ein Finger bei 1x = Swipe
    if (
      this.#scale <= MIN_ZOOM &&
      this.#isSwipeCandidate
    ) {
      preventDefault(event);

      this.#swipeDelta = {
        x:
          touch.clientX -
          this.#swipeStart.x,
        y:
          touch.clientY -
          this.#swipeStart.y,
      };

      return;
    }

    // Ein Finger bei Zoom >1 = Drag
    if (this.#isDragging) {
      preventDefault(event);

      this.#processDragGesture(touch);
    }
  };

  /**
   * @param {Touch} touch1
   * @param {Touch} touch2
   */
  #processZoomGesture(touch1, touch2) {
    const midX =
      (touch1.clientX +
        touch2.clientX) /
      2;

    const midY =
      (touch1.clientY +
        touch2.clientY) /
      2;

    const currentDistance =
      getDistance(
        touch1,
        touch2
      );

    const oldScale = this.#scale;

    const newScale =
      (currentDistance /
        this.#initialDistance) *
      this.#startScale;

    this.#scale = clamp(
      newScale,
      MIN_ZOOM,
      MAX_ZOOM
    );

    this.#hasManualZoom = true;

    const containerCenterX =
      this.clientWidth / 2;

    const containerCenterY =
      this.clientHeight / 2;

    const distanceFromCenterX =
      midX - containerCenterX;

    const distanceFromCenterY =
      midY - containerCenterY;

    const scaleDelta =
      this.#scale / oldScale - 1;

    this.#translate.x -=
      (distanceFromCenterX *
        scaleDelta) /
      this.#scale;

    this.#translate.y -=
      (distanceFromCenterY *
        scaleDelta) /
      this.#scale;

    this.#requestUpdateTransform();
    this.#isDragging = false;
  }

  /**
   * @param {Touch} touch
   */
  #processDragGesture(touch) {
    const distance = getDistance(
      touch,
      this.#startPosition
    );

    if (
      !this.#hasDraggedBeyondThreshold &&
      distance < DRAG_THRESHOLD
    ) {
      return;
    }

    this.#hasDraggedBeyondThreshold = true;

    const dx =
      touch.clientX -
      this.#startPosition.x;

    const dy =
      touch.clientY -
      this.#startPosition.y;

    this.#translate.x =
      this.#startTranslate.x +
      dx / this.#scale;

    this.#translate.y =
      this.#startTranslate.y +
      dy / this.#scale;

    this.#requestUpdateTransform();
  }

  /**
   * @param {TouchEvent} event
   */
  #handleTouchEnd = (event) => {
    if (event.touches.length !== 0) return;

    if (
      this.#isSwipeCandidate &&
      this.#scale <= MIN_ZOOM
    ) {
      const dx = this.#swipeDelta.x;
      const dy = this.#swipeDelta.y;

      const horizontalSwipe =
        Math.abs(dx) >= SWIPE_THRESHOLD &&
        Math.abs(dx) >
          Math.abs(dy) * 1.15;

      if (horizontalSwipe) {
        this.#goToSiblingMedia(
          dx < 0 ? 1 : -1
        );
      }

      this.#isSwipeCandidate = false;

      this.#swipeDelta = {
        x: 0,
        y: 0,
      };
    }

    this.#isDragging = false;
    this.#requestUpdateTransform();
    this.#hasDraggedBeyondThreshold = false;
  };

  /**
   * 1 = nächstes Bild
   * -1 = vorheriges Bild
   *
   * @param {number} direction
   */
  #goToSiblingMedia(direction) {
    const currentItem =
      this.closest(
        'li.product-media-container'
      );

    const gallery =
      this.closest(
        '.dialog-zoomed-gallery'
      );

    if (!currentItem || !gallery) return;

    const items =
      Array.from(
        gallery.children
      ).filter(
        (item) =>
          item instanceof HTMLElement &&
          item.classList.contains(
            'product-media-container'
          )
      );

    const currentIndex =
      items.indexOf(currentItem);

    if (currentIndex < 0) return;

    const targetIndex =
      currentIndex + direction;

    const target =
      items[targetIndex];

    if (!(target instanceof HTMLElement)) {
      return;
    }

    target.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'start',
    });
  }

  #constrainTranslation() {
    const containerWidth =
      this.clientWidth;

    const containerHeight =
      this.clientHeight;

    if (
      !containerWidth ||
      !containerHeight ||
      !this.#image
    ) {
      return;
    }

    this.#scale = clamp(
      this.#scale,
      MIN_ZOOM,
      MAX_ZOOM
    );

    if (this.#scale <= MIN_ZOOM) {
      this.#translate.x = 0;
      this.#translate.y = 0;

      return;
    }

    const wrapperRect =
      this.getBoundingClientRect();

    const imageElement =
      this.#image;

    let naturalWidth;
    let naturalHeight;

    if (
      imageElement.naturalWidth > 0 &&
      imageElement.naturalHeight > 0
    ) {
      naturalWidth =
        imageElement.naturalWidth;

      naturalHeight =
        imageElement.naturalHeight;
    } else {
      naturalWidth =
        wrapperRect.width;

      naturalHeight =
        wrapperRect.width;
    }

    const imageAspectRatio =
      naturalWidth /
      naturalHeight;

    const wrapperAspectRatio =
      wrapperRect.width /
      wrapperRect.height;

    let actualImageWidth;
    let actualImageHeight;

    if (
      imageAspectRatio >
      wrapperAspectRatio
    ) {
      actualImageWidth =
        wrapperRect.width;

      actualImageHeight =
        actualImageWidth /
        imageAspectRatio;
    } else {
      actualImageHeight =
        wrapperRect.height;

      actualImageWidth =
        actualImageHeight *
        imageAspectRatio;
    }

    const scaledImageWidth =
      actualImageWidth *
      this.#scale;

    const scaledImageHeight =
      actualImageHeight *
      this.#scale;

    const horizontalOverflow =
      Math.max(
        0,
        scaledImageWidth -
          wrapperRect.width
      );

    const verticalOverflow =
      Math.max(
        0,
        scaledImageHeight -
          wrapperRect.height
      );

    const maxTranslateX =
      horizontalOverflow /
      2 /
      this.#scale;

    const maxTranslateY =
      verticalOverflow /
      2 /
      this.#scale;

    this.#translate.x = clamp(
      this.#translate.x,
      -maxTranslateX,
      maxTranslateX
    );

    this.#translate.y = clamp(
      this.#translate.y,
      -maxTranslateY,
      maxTranslateY
    );

    this.style.setProperty(
      '--drag-zoom-scale',
      this.#scale.toString()
    );

    this.style.setProperty(
      '--drag-zoom-translate-x',
      `${this.#translate.x}px`
    );

    this.style.setProperty(
      '--drag-zoom-translate-y',
      `${this.#translate.y}px`
    );
  }

  #requestUpdateTransform = () => {
    if (!this.#animationFrame) {
      this.#animationFrame =
        requestAnimationFrame(
          this.#updateTransform
        );
    }
  };

  #cancelAnimationFrame() {
    if (this.#animationFrame) {
      cancelAnimationFrame(
        this.#animationFrame
      );

      this.#animationFrame = null;
    }
  }

  #updateTransform = () => {
    this.#animationFrame = null;

    this.#constrainTranslation();

    this.style.setProperty(
      '--drag-zoom-scale',
      this.#scale.toString()
    );

    this.style.setProperty(
      '--drag-zoom-translate-x',
      `${this.#translate.x}px`
    );

    this.style.setProperty(
      '--drag-zoom-translate-y',
      `${this.#translate.y}px`
    );
  };

  #resetZoom = () => {
    this.#scale = MIN_ZOOM;
    this.#startScale = MIN_ZOOM;

    this.#translate.x = 0;
    this.#translate.y = 0;

    this.#startPosition = {
      x: 0,
      y: 0,
    };

    this.#startTranslate = {
      x: 0,
      y: 0,
    };

    this.#isDragging = false;

    this.#lastTapTime = 0;
    this.#lastTapPosition = null;

    this.#hasDraggedBeyondThreshold = false;
    this.#hasManualZoom = false;

    this.#isSwipeCandidate = false;

    this.#swipeStart = {
      x: 0,
      y: 0,
    };

    this.#swipeDelta = {
      x: 0,
      y: 0,
    };

    this.style.setProperty(
      '--drag-zoom-scale',
      MIN_ZOOM.toString()
    );

    this.style.setProperty(
      '--drag-zoom-translate-x',
      '0px'
    );

    this.style.setProperty(
      '--drag-zoom-translate-y',
      '0px'
    );
  };

  destroy() {
    this.#controller.abort();
    this.#cancelAnimationFrame();
  }
}

function getDistance(point1, point2) {
  const x1 =
    /** @type {Point} */ (point1).x ??
    /** @type {Touch} */ (point1).clientX;

  const y1 =
    /** @type {Point} */ (point1).y ??
    /** @type {Touch} */ (point1).clientY;

  const x2 =
    /** @type {Point} */ (point2).x ??
    /** @type {Touch} */ (point2).clientX;

  const y2 =
    /** @type {Point} */ (point2).y ??
    /** @type {Touch} */ (point2).clientY;

  const dx = x1 - x2;
  const dy = y1 - y2;

  return Math.sqrt(
    dx * dx +
    dy * dy
  );
}

if (
  !customElements.get(
    'drag-zoom-wrapper'
  )
) {
  customElements.define(
    'drag-zoom-wrapper',
    DragZoomWrapper
  );
}

/**
 * @typedef {Object} Point
 * @property {number} x
 * @property {number} y
 */